/**
 * Base URL for property intelligence (`/api/property/intelligence`, no trailing slash on host).
 * Set `VITE_BASE_URI` in env (e.g. https://dev.jaaga.ai).
 */
export function getIntelligenceApiBase() {
  const base = String(import.meta.env.VITE_BASE_URI ?? "").replace(/\/$/, "");
  return `${base}/api/property/intelligence`;
}

/** sessionStorage key for retry after purchasing credits */
export const INTEL_PENDING_RETRY_KEY = "jaaga_intel_pending_action_v1";

/** Bootstrap Report Suite with a preloaded report (e.g. from Search). */
export const INTEL_SEED_REPORT_KEY = "jaaga_intel_seed_report_v1";

function parseAuthToken() {
  const raw = localStorage.getItem("token");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * POST /api/property/intelligence/actions (requires X-Auth-Token).
 * @returns {{ ok: true, data: unknown } | { ok: false, error: string }}
 */
export async function postIntelligenceAction(body) {
  const base = getIntelligenceApiBase();
  const token = parseAuthToken();
  const userId = localStorage.getItem("userId");
  const res = await fetch(`${base}/actions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "X-Auth-Token": token } : {}),
    },
    body: JSON.stringify(userId ? { ...body, userId } : body),
  });
  let json = {};
  try {
    json = await res.json();
  } catch {
    /* ignore */
  }
  if (res.status === 403 && json?.error === "INSUFFICIENT_CREDITS") {
    return { ok: false, error: "INSUFFICIENT_CREDITS", status: res.status };
  }
  if (res.status === 401) {
    return { ok: false, error: "UNAUTHORIZED", status: res.status };
  }
  if (!res.ok) {
    const msg =
      json?.message ||
      json?.status?.message ||
      json?.error ||
      `Request failed (${res.status})`;
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return { ok: true, data: json.data, status: res.status };
}

/** Authenticated fetch for per-user intelligence endpoints (watchlist). */
async function authFetch(path, { method = "GET", body } = {}) {
  const base = getIntelligenceApiBase();
  const token = parseAuthToken();
  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "X-Auth-Token": token } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let json = {};
  try {
    json = await res.json();
  } catch {
    /* ignore */
  }
  if (res.status === 401) return { ok: false, error: "UNAUTHORIZED" };
  if (!res.ok) throw new Error(json?.error || `Request failed (${res.status})`);
  return { ok: true, data: json };
}

export const watchlistApi = {
  list: () => authFetch("/watchlist"),
  add: (item) => authFetch("/watchlist", { method: "POST", body: item }),
  remove: (id) => authFetch(`/watchlist/${id}`, { method: "DELETE" }),
  markSeen: (id) => authFetch(`/watchlist/${id}/seen`, { method: "POST" }),
};
